// Project CRUD service.
// Endpoint shape: POST /create, POST /edit/:id, DELETE /delete/:id, GET /list, GET /:id.
// All requests go through `apiCall` from src/services/api.js (auto-injects Bearer token).
//
// If floorPlanRef is a base64 data-URL the payload is sent as multipart/form-data so
// the WAF does not block it. The image travels as a binary file part; the server decodes
// it back to base64 and stores it in the DB text column.
import { apiCall } from '../../../../../services/api';

/**
 * Convert a base64 data-URL into a File blob.
 * Returns null if the string is not a data-URL.
 */
const dataUrlToFile = (dataUrl, filename = 'floor_plan') => {
  if (!dataUrl || !dataUrl.startsWith('data:')) return null;
  const [header, b64] = dataUrl.split(',');
  const mime = (header.match(/:(.*?);/) || [])[1] || 'image/jpeg';
  const ext  = mime.split('/')[1] || 'jpg';
  const bytes = atob(b64);
  const arr   = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], `${filename}.${ext}`, { type: mime });
};

/**
 * Build the request body.
 * - If floorPlanRef is a base64 data-URL → FormData (bypasses WAF), floor plan travels
 *   as a binary file part.
 * - If config itself contains embedded base64 (e.g. captured layer images in
 *   config.layers.layerImages — see payloadFormatter.js) → also FormData, but with
 *   config sent as a plain text field, since the WAF blocks large base64 strings
 *   embedded in a JSON body but not the same bytes travelling as multipart fields.
 *   This became reachable once canvas layer capture started actually succeeding
 *   (previously captureLayerImage() silently failed on a tainted canvas, so
 *   layerImages stayed empty and never tripped this).
 * - Otherwise → plain JSON string.
 */
const buildBody = (payload) => {
  const { floorPlanRef, config, ...rest } = payload;

  const file = dataUrlToFile(floorPlanRef, 'floor_plan');
  const configJson = JSON.stringify(config ?? {});
  const configHasBase64 = configJson.includes('data:image');

  // eslint-disable-next-line no-console
  console.log('SAVE_PAYLOAD_DEBUG', {
    payload,
    configSizeBytes: configJson.length,
    configSizeMB: (configJson.length / (1024 * 1024)).toFixed(2),
    hasFloorPlanFile: !!file,
    configHasBase64,
  });

  if (file || configHasBase64) {
    const fd = new FormData();
    // Scalar fields
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== null && v !== undefined) fd.append(k, String(v));
    });
    // Config as JSON string (backend decodes it) — as a form field, not raw JSON body,
    // so embedded base64 inside it doesn't get WAF-inspected the same way.
    fd.append('config', configJson);
    if (file) {
      // Floor plan as binary file (not base64 text → WAF won't trigger)
      fd.append('floorPlan', file);
    } else if (floorPlanRef) {
      // Not a fresh upload — just carry the existing URL through as a plain field.
      fd.append('floorPlanRef', floorPlanRef);
    }
    return fd;
  }

  // No base64 anywhere → safe to send as JSON
  return JSON.stringify(payload);
};

/**
 * Fetch a saved floor plan image as a base64 data-URL instead of loading the raw
 * storage URL directly. The CDN in front of the API intercepts any URL whose *path*
 * ends in an image extension (.png etc) as a static asset and never forwards it to
 * Laravel at all — no CORS headers, preflight fails outright. Routing the bytes
 * through JSON (filename passed as a query param, so the path itself never ends in
 * .png) sidesteps that: a data: URL is always same-origin, so the canvas stays
 * exportable regardless of what the CDN does to image-shaped URLs.
 * Falls back to the raw URL if this fails (still displays; may not be exportable).
 */
const resolveFloorPlanImage = async (floorPlanRef) => {
  if (!floorPlanRef || !floorPlanRef.includes('/api/floor-plan-image/')) {
    return floorPlanRef; // not our own API-served URL (e.g. old direct storage URL, or data: URL already)
  }
  try {
    const filename = floorPlanRef.split('/api/floor-plan-image/')[1];
    const response  = await apiCall(`/floor-plan-image-data?filename=${encodeURIComponent(filename)}`, { method: 'GET' });
    return response?.data || floorPlanRef;
  } catch {
    return floorPlanRef;
  }
};

/**
 * Same CDN-bypass resolution as resolveFloorPlanImage, but for offloaded
 * report-layer images (config.layers.layerImages — see LayerImageStorage on
 * the backend). Older/not-yet-saved layers still hold a raw base64 data-URL
 * in memory — those are returned as-is, no network round-trip needed.
 */
const resolveLayerImage = async (layerImageRef) => {
  if (!layerImageRef || typeof layerImageRef !== 'string' || !layerImageRef.includes('/api/layer-image/')) {
    return layerImageRef; // already a data: URL, empty, or not our own API-served URL
  }
  try {
    const filename = layerImageRef.split('/api/layer-image/')[1];
    const response  = await apiCall(`/layer-image-data?filename=${encodeURIComponent(filename)}`, { method: 'GET' });
    return response?.data || layerImageRef;
  } catch {
    return layerImageRef;
  }
};

/**
 * Merge a small batch of layerImages into an already-saved project, instead of
 * replacing the whole config. Used to chunk very large saves (many enabled
 * report layers, each holding a freshly-captured base64 image) into several
 * small requests — one giant multi-MB request is what the WAF/PHP post-size
 * limit and Chrome's own Network-panel payload viewer both choke on.
 * Always sent as FormData (same WAF-bypass reasoning as buildBody above),
 * since a chunk still typically contains base64 image data.
 */
const mergeLayerImages = (id, layerImagesChunk) => {
  const fd = new FormData();
  fd.append('layerImages', JSON.stringify(layerImagesChunk));
  return apiCall(`/edit/${id}/layer-images`, { method: 'POST', body: fd });
};

const CHUNK_SIZE = 5;
// Chunks are also capped by payload size: report layers are captured as large
// base64 PNGs (several MB each), so "10 images" could mean a 50-100 MB request
// that the host's post-size / WAF / timeout limits reject — that is what made a
// save fail with "N of M report-image batches failed". Anything bigger than the
// cap simply travels alone.
const MAX_CHUNK_CHARS = 1.5 * 1024 * 1024;
const entrySize = (v) => (typeof v === 'string' ? v.length : JSON.stringify(v ?? '').length);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Split layerImages into batches (at most CHUNK_SIZE entries AND roughly
 * MAX_CHUNK_CHARS of data each) and merge them into the project one request at
 * a time. Deliberately sequential, not parallel — parallel chunks all target
 * the same project row, and the backend's row lock (lockForUpdate, needed so
 * concurrent chunks can't silently drop each other's data) then makes them
 * queue behind one another anyway. On this host's request-timeout limits that
 * queueing was enough to 504 some of them.
 *
 * A batch that fails is retried once, then split into single-image requests,
 * so one heavy image no longer sinks the images travelling with it. Every
 * batch is still attempted even if an earlier one fails, and if any image
 * ultimately could not be saved this throws once all have been attempted
 * (a client reported reports needing to be regenerated after reopening a
 * project — exactly what a silently-dropped chunk causes).
 *
 * `onProgress(done, total)` fires after each batch (success or failure).
 */
const saveLayerImagesInChunks = async (id, layerImages, onProgress) => {
  const entries = Object.entries(layerImages || {});
  const chunks = [];
  let cur = [];
  let curChars = 0;
  entries.forEach(([key, value]) => {
    const size = entrySize(value);
    if (cur.length && (cur.length >= CHUNK_SIZE || curChars + size > MAX_CHUNK_CHARS)) {
      chunks.push(cur);
      cur = [];
      curChars = 0;
    }
    cur.push([key, value]);
    curChars += size;
  });
  if (cur.length) chunks.push(cur);

  const trySend = async (pairs) => {
    try {
      await mergeLayerImages(id, Object.fromEntries(pairs));
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Layer-image request failed:', e);
      return false;
    }
  };

  let failedCount = 0;
  for (let i = 0; i < chunks.length; i++) {
    const pairs = chunks[i];
    // eslint-disable-next-line no-await-in-loop
    let ok = await trySend(pairs);
    if (!ok) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(600);
      if (pairs.length > 1) {
        // one heavy image shouldn't fail the rest of its batch — resend them singly
        let allOk = true;
        for (let j = 0; j < pairs.length; j++) {
          // eslint-disable-next-line no-await-in-loop
          const single = (await trySend([pairs[j]])) || (await sleep(600), await trySend([pairs[j]]));
          if (!single) allOk = false;
        }
        ok = allOk;
      } else {
        // eslint-disable-next-line no-await-in-loop
        ok = await trySend(pairs);
      }
    }
    if (!ok) failedCount += 1;
    if (onProgress) onProgress(i + 1, chunks.length);
  }
  if (failedCount > 0) {
    throw new Error(
      `${failedCount} of ${chunks.length} report-image batch${chunks.length > 1 ? 'es' : ''} failed to save. ` +
      `Please try saving again — the affected reports may need to be reopened and saved if this keeps happening.`
    );
  }
};

const projectService = {
  list: ({ page = 1, perPage = 12, search = '' } = {}) => {
    const params = new URLSearchParams({ page, per_page: perPage });
    if (search) params.set('search', search);
    return apiCall(`/list?${params.toString()}`, { method: 'GET' });
  },

  get: (id) => apiCall(`/${id}`, { method: 'GET' }),

  resolveFloorPlanImage,
  resolveLayerImage,
  saveLayerImagesInChunks,

  create: (payload) =>
    apiCall('/create', { method: 'POST', body: buildBody(payload) }),

  edit: (id, payload) =>
    apiCall(`/edit/${id}`, { method: 'POST', body: buildBody(payload) }),

  delete: (id) => apiCall(`/delete/${id}`, { method: 'DELETE' }),
};

export default projectService;
